// Single source of truth for the music-info-server identifier.
//
// The API exposes the identifier as `id` (music-info-server's prepareForPublicApi
// rewrites its internal public_id → id). Historically, different surfaces carried
// it under publicId / public_id / track_public_id / mix_public_id / artist_public_id.
// This is the ONE place that knows those legacy names: normalize at every data
// boundary with withId(obj), then read `.id` everywhere downstream.
//
// NOTE: locally-built objects (e.g. DJ-library SQLite tracks) have no music-info
// id — `.id` is legitimately null there, and that's the only case the feed falls
// back to text. Don't fabricate an id for those.

// Never returns a number or an all-digit string: a local SQLite track
// (integer `.id`) or a mapMix row (numeric internal id + publicId) must not
// be sent to the server as a catalogue id (audit 2026-10-20). Explicit public
// fields win over `.id`; a numeric `.id` yields null, not the number.
const base62OrNull = (v) => {
    if (typeof v !== 'string') return null;
    const s = v.trim();
    return s && !/^\d+$/.test(s) ? s : null;
};

export function entityId(obj) {
    if (!obj) return null;
    for (const v of [obj.publicId, obj.public_id, obj.djpalId, obj.id,
        obj.track_public_id, obj.mix_public_id, obj.artist_public_id]) {
        const s = base62OrNull(v);
        if (s) return s;
    }
    return null;
}

// Strict variant for fields that must hold a music-info PUBLIC id (a base62
// STRING): explicit public fields are checked before `.id`, and numeric values
// are rejected. Since 2026-08-12 the server never exports a numeric mix id
// (mapMix id = public_id; the mixId side-channel leak is fixed), but numeric
// ids still reach this code from old localStorage snapshots, server-persisted
// rows written before the fix, and any renderer-built shape that copies a
// row id — and the server's write-boundary guard (catalogIdOrNull) silently
// nulls a numeric id, storing an identity-less row. Use this wherever the
// value is persisted or sent as a *_public_id.
export function publicEntityId(obj) {
    if (!obj) return null;
    for (const v of [obj.publicId, obj.public_id, obj.mix_public_id,
        obj.track_public_id, obj.artist_public_id, obj.id]) {
        if (typeof v === 'string') {
            const s = v.trim();
            if (s && !/^\d+$/.test(s)) return s;
        }
    }
    return null;
}

// Returns a shallow copy with `.id` populated from any legacy id field. Use at
// ingestion boundaries (API/IPC responses, redux store writes) so the rest of the
// app can read `.id` exclusively.
export function withId(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const id = entityId(obj);
    return id && id !== obj.id ? { ...obj, id } : obj;
}

export const withIds = (arr) => (Array.isArray(arr) ? arr.map(withId) : arr);
